import { download, generateCsv, mkConfig } from 'export-to-csv';
import { create } from 'zustand';

import { SubmissionsService } from '@/services/submissions/service';
import {
  CandidateSummary,
  ExportToCopilotResponse,
  Pagination,
  RatingResponse,
  Submission,
  SubmissionFilters,
  SubmissionQueryParams,
  SubmissionSort,
} from '@repo/api/open-calls/graphql';
import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import {
  CANDIDATES_SORT_ASC_OPTION,
  CANDIDATES_SORT_DESC_OPTION,
} from '@repo/ui/constants/index';
import { OpenCallModel } from '@repo/ui/models/open-call';
import { createInitialState, InitialState, STAGES } from '@repo/ui/state';
import {
  emptyFilterValue,
  getFilterSummaryKey,
  getFilterValue,
  isFilterValueActive,
  type NavigationFilterDefinition,
} from '@repo/ui/helpers/navigation-filter-definitions';
import _ from 'lodash';
import { spSearchFilter } from '@/services/tracking/snowplow-script';

const MAX_MOVE_SIZE = 100;

export interface CandidatesStore extends InitialState<Submission[]> {
  _ids: string[];
  count: number;
  params: SubmissionQueryParams;
  summary: CandidateSummary;
  loadingSpinner: boolean;
  paramsByStage: Record<string, SubmissionQueryParams>;

  updateCandidate: (candidate: Submission) => void;
  updateCandidateRating: (_id: string, rating: number, userId: string) => void;
  fetchCandidates: (
    queryParams: SubmissionQueryParams,
    silent?: boolean,
  ) => Promise<void>;
  resetFilters: (filterDefinitions?: NavigationFilterDefinition[]) => void;
  areDefaultFiltersActive: (
    filterDefinitions?: NavigationFilterDefinition[],
  ) => boolean;
  updateFilters: (
    filters:
      | SubmissionFilters
      | ((filters: SubmissionFilters) => SubmissionFilters),
  ) => Promise<void>;
  hasActiveFilters: (
    params?: {
      filters?: SubmissionFilters | undefined;
    },
    filterDefinitions?: NavigationFilterDefinition[],
  ) => boolean;
  updateSort: (
    sortUpdater: SubmissionSort | ((sort: SubmissionSort) => SubmissionSort),
  ) => Promise<void>;
  updateQueryParams: (
    stageId: keyof typeof paramsByStage,
    params: SubmissionQueryParams,
    silent?: boolean,
  ) => Promise<void>;
  updatePagination: (
    pagination: Pagination | ((pagination: Pagination) => Pagination),
  ) => Promise<void>;
  goToStage: (
    stageId: keyof typeof paramsByStage,
    openCallId?: string,
  ) => Promise<void>;
  moveToStage: (
    candidateIds: string[],
    targetStageId: string,
    userId: string,
  ) => Promise<number>;
  contactExport: (candidateIds: string[]) => Promise<void>;
  reset: () => void;
  exportCandidatesToCopilot: (
    candidateIds: string[],
    openCall: OpenCallModel,
  ) => Promise<ExportToCopilotResponse | Error>;
}

export const dehydratedFilterSummary: CandidateSummary = {
  ages: undefined,
  cities: undefined,
  nationalities: undefined,
  isRepresented: undefined,
};

const defaultFilters: Partial<SubmissionFilters> = {
  isRepresented: false,
  ages: [],
  cities: [],
  nationalities: [],
};

const defaultStageParams = (
  stageId: string = STAGES.applicants.id,
): SubmissionQueryParams => ({
  sort: CANDIDATES_SORT_DESC_OPTION, //? MongoDB: 1 => First; -1 => Latest
  filters: {
    stageId,
    ...defaultFilters,
  } as SubmissionFilters,
  pagination: { page: 0, limit: 24, skip: 0 } as Pagination,
});

const isConfiguredFilterActive = ({
  definition,
  filters,
  summary,
}: {
  definition: NavigationFilterDefinition;
  filters: Record<string, unknown>;
  summary: CandidateSummary;
}) => {
  const value = getFilterValue(filters, definition);

  if (definition.kind === 'range' && Array.isArray(value)) {
    const summaryRange = summary[
      getFilterSummaryKey(definition) as keyof CandidateSummary
    ] as { range?: number[] } | undefined;
    const [min, max] = value.map(Number);
    const [summaryMin, summaryMax] = summaryRange?.range || [];

    if (min === summaryMin && max === summaryMax) return false;
  }

  return isFilterValueActive(value, definition);
};

const paramsByStage: Record<string, SubmissionQueryParams> = {
  [STAGES.applicants.id]: defaultStageParams(),
  [STAGES.jury.id]: {
    sort: CANDIDATES_SORT_ASC_OPTION,
    pagination: null,
    filters: {
      stageId: STAGES.jury.id,
      ...defaultFilters,
    } as SubmissionFilters,
  },
  [STAGES.finalists.id]: {
    sort: CANDIDATES_SORT_ASC_OPTION,
    pagination: null,
    filters: {
      stageId: STAGES.finalists.id,
      ...defaultFilters,
    } as SubmissionFilters,
  },
  [STAGES.dismissed.id]: {
    sort: CANDIDATES_SORT_ASC_OPTION,
    pagination: { page: 0, limit: 24, skip: 0 } as Pagination,
    filters: {
      stageId: STAGES.dismissed.id,
      ...defaultFilters,
    } as SubmissionFilters,
  },
};

export const initialState = {
  _ids: [],
  count: 0,
  loadingSpinner: true,
  paramsByStage,
  params: paramsByStage[STAGES.applicants.id],
  summary: dehydratedFilterSummary,
  ...createInitialState<Array<Submission>>([]),
};

// Only the latest list request may publish results, errors, or loading state.
let requestVersion = 0;

export const store = create<CandidatesStore>((set, store) => ({
  ...initialState,
  count: initialState.count,
  params: initialState.params!,
  summary: initialState.summary,
  loadingSpinner: initialState.loadingSpinner,

  fetchCandidates: async (params, silent = false) => {
    const version = ++requestVersion;
    set((state) => ({
      ...state,
      loading: !silent,
      loadingSpinner: true,
      data: silent ? state.data : [],
    }));
    try {
      const response = await SubmissionsService.querySubmissionsBy(params);
      if (version !== requestVersion) return;

      set((state) => ({
        ...state,
        error: null,
        _ids: response._ids,
        data: response.data,
        count: response.count,
        summary: response.summary,
      }));
    } catch (error: unknown) {
      if (version !== requestVersion) return;
      showErrorToast({ title: 'Unable to load candidates' });
      set((state) => ({
        ...state,
        error: error as unknown as string,
      }));
    } finally {
      if (version === requestVersion)
        set((state) => ({
          ...state,
          loading: false,
          loadingSpinner: false,
        }));
    }
  },

  updateCandidate: (candidate) => {
    set((state) => ({
      ...state,
      data: state.data.map((c) => (c._id === candidate._id ? candidate : c)),
    }));
  },
  updateCandidateRating: (_id, rating, userId) => {
    const getRatingData = (c: Submission) =>
      ({
        rating,
        storyID: c._id,
        authorID: userId,
      }) as RatingResponse;

    set((state) => ({
      ...state,
      data: state.data.map((c) =>
        c._id === _id
          ? {
              ...c,
              rating: getRatingData(c),
              ratings: [...(c.ratings || []), getRatingData(c)],
            }
          : c,
      ),
    }));
  },

  updateQueryParams: async (stageId, params, silent = false) => {
    set((state) => ({
      ...state,
      params,
      paramsByStage: { ...store().paramsByStage, [stageId]: params },
    }));

    // process filters
    const filters = Object.fromEntries(
      Object.entries(params.filters).filter(([, value]) => value),
    ) as SubmissionFilters;

    // process pagination
    let pagination: Pagination | null = null;
    if (params.pagination) {
      pagination = {
        page: params.pagination.page,
        limit: params.pagination.limit,
        skip: params.pagination.page * params.pagination.limit,
      };
    }

    // process sort
    const sort: SubmissionSort = params.sort;

    const queryParams: SubmissionQueryParams = {
      filters,
      pagination,
      sort,
    };

    await store().fetchCandidates(queryParams, silent);
  },

  //? vs switchToStage(stageId) vs toggleStage(stageId)
  goToStage: async (stageId, openCallId) => {
    const stageParams =
      store().paramsByStage[stageId] ?? defaultStageParams(stageId);
    await store().updateQueryParams(stageId, {
      ...stageParams,
      filters: { ...stageParams.filters, openCallId: openCallId! },
    });
  },

  areDefaultFiltersActive: (filterDefinitions = []) => {
    const { filters } = store().params;

    return filterDefinitions.every(
      (definition) =>
        !isConfiguredFilterActive({
          definition,
          filters: filters as Record<string, unknown>,
          summary: store().summary,
        }),
    );
  },

  resetFilters: (filterDefinitions = []) => {
    const { filters } = store().params;
    const resetFilterValues = Object.fromEntries(
      filterDefinitions.map((definition) => [
        definition.key,
        emptyFilterValue(definition),
      ]),
    );

    store().updateFilters({ ...filters, ...resetFilterValues });
    return store();
  },

  updateFilters: async (filtersUpdater) => {
    const currentFilters = store().params.filters;

    let updatedFilters: SubmissionFilters;
    if (typeof filtersUpdater === 'function') {
      updatedFilters = filtersUpdater(currentFilters);
    } else {
      updatedFilters = filtersUpdater;
    }

    if (!updatedFilters.openCallId || !updatedFilters.stageId) {
      throw new Error('openCallId and stageId are required in filters');
    }

    let updatedParams: SubmissionQueryParams;
    //? Reset pagination to the first page when new filters are applied to ensure users do not miss the first batch of results
    if (store().params.pagination) {
      updatedParams = {
        ...store().params,
        filters: updatedFilters,
        pagination: {
          ...(store().params.pagination as Pagination),
          page: 0,
        },
      };
    } else {
      updatedParams = {
        ...store().params,
        filters: updatedFilters,
      };
    }

    await store().updateQueryParams(
      store().params.filters.stageId,
      updatedParams,
      true,
    );
    const {
      params: { filters, sort },
      count,
      data,
    } = store();
    spSearchFilter(
      filters,
      count,
      Object.entries(sort)
        .map(([k, v]) => `${k}:${v?.name}`)
        .join(','),
      'updateFilters',
      data.map((d) => d._id),
    );
  },

  hasActiveFilters: (
    params?: { filters?: SubmissionFilters },
    filterDefinitions = [],
  ): boolean => {
    if (!params?.filters) return false;

    return filterDefinitions.some((definition) =>
      isConfiguredFilterActive({
        definition,
        filters: params.filters as Record<string, unknown>,
        summary: store().summary,
      }),
    );
  },

  updatePagination: async (paginationUpdater) => {
    const currentPagination = store().params.pagination;

    let updatedPagination: Pagination;
    if (typeof paginationUpdater === 'function') {
      updatedPagination = paginationUpdater(currentPagination!);
    } else {
      updatedPagination = paginationUpdater;
    }

    const updatedParams: SubmissionQueryParams = {
      ...store().params,
      pagination: updatedPagination,
    };

    await store().updateQueryParams(
      store().params.filters.stageId,
      updatedParams,
    );
    const {
      params: { filters, sort },
      count,
      data,
    } = store();
    spSearchFilter(
      filters,
      count,
      Object.entries(sort)
        .map(([k, v]) => `${k}:${v?.name}`)
        .join(','),
      'updatePagination',
      data.map((d) => d._id),
    );
  },

  updateSort: async (sortUpdater) => {
    const currentSort = store().params.sort;

    let updatedSort: SubmissionSort;
    if (typeof sortUpdater === 'function') {
      updatedSort = sortUpdater(currentSort);
    } else {
      updatedSort = sortUpdater;
    }

    const updatedParams: SubmissionQueryParams = {
      ...store().params,
      sort: updatedSort,
    };

    await store().updateQueryParams(
      store().params.filters.stageId,
      updatedParams,
      true,
    );
    const {
      params: { filters, sort },
      count,
      data,
    } = store();
    spSearchFilter(
      filters,
      count,
      Object.entries(sort)
        .map(([k, v]) => `${k}:${v?.name}`)
        .join(','),
      'updateSort',
      data.map((d) => d._id),
      'sort',
    );
  },

  contactExport: async (candidateIds) => {
    try {
      const csvConfig = mkConfig({
        useKeysAsHeaders: true,
        filename: 'candidates',
      });
      const data = await SubmissionsService.contactExport(candidateIds);
      const csv = generateCsv(csvConfig)(data);
      download(csvConfig)(csv);
    } catch (error) {
      showErrorToast({ title: 'Unable to export contact data' });
      console.error('Failed to export:', error);
    }
  },

  moveToStage: async (candidateIds, targetStageId, userId) => {
    try {
      const state = store();
      const chunks = _.chunk(candidateIds, MAX_MOVE_SIZE);
      let sum = 0;
      for (const chunk of chunks) {
        sum += await SubmissionsService.moveToStage(
          chunk,
          targetStageId,
          userId,
        );
      }
      set((state) => ({
        ...state,
        count: state.count - candidateIds.length,
      }));
      state.fetchCandidates(
        { ...state.params, excludeIds: candidateIds },
        true,
      );
      return sum;
    } catch (error: unknown) {
      showErrorToast({ title: 'Unable to move candidate' });
      set((state) => ({
        ...state,
        error: (error as Error).message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
    return 0;
  },
  reset: () => {
    ++requestVersion;
    set((state) => ({
      ...state,
      _ids: initialState._ids,
      data: initialState.data,
      count: initialState.count,
      loading: initialState.loading,
      loadingSpinner: initialState.loadingSpinner,
      error: initialState.error,
      params: paramsByStage[store().params.filters.stageId],
      paramsByStage,
      summary: initialState.summary,
    }));
  },

  exportCandidatesToCopilot: async (candidateIds, openCall) => {
    try {
      return await SubmissionsService.exportFinalistsToCopilot(candidateIds, {
        id: openCall.id,
        name: openCall.name,
      });
    } catch (error) {
      return error as Error;
    }
  },
}));
